// 261010 Epic 2 / P2b-3 — hàm THUẦN (không antd / router / axios) dựng `ops` cho `PUT /plans/:id/batch-edit` từ bản nháp sửa tay MỘT video ở khung mới
// (bước 1 Kịch bản, bước 3 Đạo diễn & diễn xuất). Whitelist trường theo Phase doc Rev 2 §1; contract thật ở BE `filmEventRepository.applyBatchEdits`.
//
// Bốn điều phải giữ (mỗi điều có test):
//  1. Chỉ trường TRONG WHITELIST vào ops và vào dấu vân tay — nháp có thêm `ffDesc`, `kind`, `motion`… cũng KHÔNG đi lên.
//  2. `replaceDialogue` luôn gửi ĐỦ danh sách, dòng cũ giữ nguyên `afterActionBeatOrder` (neo vào nhịp), dòng mới `null`.
//  3. Cast cấp video KHÔNG có `updateEvent.presence`: phân rã thành các `updateShot.presence` (như UI cũ — `applyEventCast` ở EventPlanTab).
//  4. Dấu vân tay = phát hiện TỐT NHẤT phía client rằng video đã bị sửa ở nơi khác. KHÔNG phải khoá: một lượt ghi rơi vào giữa lúc đọc lại và lúc PUT vẫn lọt (tech-debt CAS ở BE).
import type { BatchEditOp, EventPlan, FilmActionBeat, FilmDialogueLine, FilmDialogueMode, FilmEvent, FilmEventBeatMeta, FilmRunningJob, FilmShot } from '../../types/film';
import { planWriteBlock, JOBS_UNKNOWN_TEXT } from './workflowActionPolicy';

// ───────────────────────── mô hình nháp ─────────────────────────

/** Bản chụp có thể sửa của MỘT video. `shots` / `lines` giữ nguyên hình dạng `FilmShot` / `FilmDialogueLine` (để `EventShotCard` / `EventDialogueRow` dùng trực tiếp); chỉ các trường whitelist được xét khi so sánh / dựng op. */
export interface EditState {
  eventId: string;
  action: string | null;
  genDurationS: number | null;
  /** PEAK | NORMAL | TRANSITION (thiếu ⇒ NORMAL — đúng cái Select của UI cũ hiển thị). */
  dramaticWeight: string;
  /** 261010 P9D I4 — tactic / initiator / emotionalEvent / actionBeats sửa được ở bước 1. */
  tactic: string | null;
  initiator: string | null;
  emotionalEvent: string | null;
  actionBeats: FilmActionBeat[];
  /** Bản beatMeta lúc tải — nền để gộp (thay các field whitelist, mọi khoá khác giữ nguyên). */
  beatMeta: FilmEventBeatMeta | null;
  shots: FilmShot[];
  lines: FilmDialogueLine[];
}

export const DRAMATIC_WEIGHTS = ['PEAK', 'NORMAL', 'TRANSITION'] as const;
/**
 * Trường `updateShot` mà `ShotCard` thật sự sửa (SA r1 B1) + `performance` (261010 P3e: ô sửa ở bước Đạo diễn; BE đánh dấu `acting_cue_refs.manualFields` khi giá trị thật đổi)
 * — KHÔNG mở ffDesc / lfDesc / motion / propChange / beatKind / kind / prompt. Dùng chung với bước Kịch bản (dấu vân tay cũng gồm `performance`).
 */
export const SHOT_EDIT_FIELDS = ['shotSize', 'angle', 'cameraMove', 'framing', 'effect', 'purpose', 'frameNote', 'presence', 'screenDirection', 'performance'] as const;
type ShotField = (typeof SHOT_EDIT_FIELDS)[number];

export const normalizeWeight = (w: unknown): string => {
  const u = String(w || '').toUpperCase();
  return (DRAMATIC_WEIGHTS as readonly string[]).includes(u) ? u : 'NORMAL';
};

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v ?? null));
const byOrder = <T extends { orderIndex?: number }>(a: T, b: T) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0);

type EventLike = Pick<FilmEvent, 'id' | 'action' | 'genDurationS' | 'beatMeta' | 'shots' | 'dialogueLines'>;

/** Chụp video thành `EditState` (sao chép sâu, sắp shot / thoại theo `orderIndex`). `shots` / `lines` mặc định lấy từ chính event. */
export function snapshotOf(ev: EventLike, shots: FilmShot[] = ev.shots || [], lines: FilmDialogueLine[] = ev.dialogueLines || []): EditState {
  return {
    eventId: ev.id,
    action: ev.action ?? null,
    genDurationS: typeof ev.genDurationS === 'number' ? ev.genDurationS : null,
    dramaticWeight: normalizeWeight(ev.beatMeta?.dramaticWeight),
    tactic: ev.beatMeta?.tactic ?? null,
    initiator: ev.beatMeta?.initiator ?? null,
    emotionalEvent: ev.beatMeta?.emotionalEvent ?? null,
    actionBeats: clone(ev.beatMeta?.actionBeats ?? []),
    beatMeta: ev.beatMeta ? clone(ev.beatMeta) : null,
    shots: clone(shots).sort(byOrder),
    lines: clone(lines).sort(byOrder),
  };
}
export const cloneState = (s: EditState): EditState => clone(s);

let TMP_LINE = 0;
/** Dòng thoại mới của nháp: id tạm (chỉ làm React key; server tự sinh id thật), `afterActionBeatOrder = null` (chưa neo nhịp). Giống `addLine` của UI cũ. */
export const newLine = (): FilmDialogueLine => ({ id: `tmp-line-${++TMP_LINE}`, orderIndex: 0, speakerName: '', textEn: '', dialogueMode: 'ON_CAMERA' as FilmDialogueMode, afterActionBeatOrder: null });

// ───────────────────────── so sánh / dấu vân tay ─────────────────────────

/** JSON ổn định: khoá object được sắp, `undefined` bị bỏ — hai object cùng nội dung khác thứ tự khoá cho cùng chuỗi. */
function stable(v: unknown): string {
  const walk = (x: any): any => {
    if (Array.isArray(x)) return x.map(walk);
    if (x && typeof x === 'object') return Object.keys(x).sort().reduce<Record<string, unknown>>((o, k) => { if (x[k] !== undefined) o[k] = walk(x[k]); return o; }, {});
    return x === undefined ? null : x;
  };
  return JSON.stringify(walk(v ?? null));
}
const same = (a: unknown, b: unknown): boolean => stable(a) === stable(b);

const lineKey = (l: FilmDialogueLine) => ({
  speakerName: l.speakerName ?? null, textEn: l.textEn ?? null, dialogueMode: l.dialogueMode, afterActionBeatOrder: l.afterActionBeatOrder ?? null,
});
const shotKey = (s: FilmShot) => {
  const o: Record<string, unknown> = { id: s.id, orderIndex: s.orderIndex };
  for (const k of SHOT_EDIT_FIELDS) o[k] = (s as any)[k] ?? null;
  return o;
};

/**
 * Dấu vân tay của ĐÚNG các trường sửa được: action, giây, độ nặng kịch, thoại (nội dung + thứ tự + neo; KHÔNG id / origin / voiceId vì `replaceDialogue` tạo lại id ở mỗi lượt Lưu)
 * và shot (id + thứ tự + các trường whitelist). Không đổi khi `clips`, `staleStages`, `authorPrompt`, `ffDesc`/`lfDesc`/`motion`… hay khoá khác của `beatMeta` đổi.
 */
export function fingerprintOf(s: EditState): string {
  return stable({
    a: s.action ?? null, g: s.genDurationS ?? null, w: normalizeWeight(s.dramaticWeight),
    t: s.tactic ?? null, i: s.initiator ?? null, e: s.emotionalEvent ?? null,
    b: (s.actionBeats ?? []).map((x) => ({ order: x.order, text: x.text ?? '', kind: x.kind ?? null })),
    l: s.lines.map(lineKey),
    s: s.shots.map(shotKey),
  });
}

// ───────────────────────── nháp ⇒ ops ─────────────────────────

/**
 * Bản nháp ⇒ danh sách op `batch-edit` (chỉ trường ĐỔI; không đổi ⇒ `[]`). Thứ tự op như `buildBatchOps` của UI cũ: updateEvent → replaceDialogue → updateShot.
 * `freshBeatMeta` = beatMeta TƯƠI của server (lấy ngay trước khi lưu): `beatMeta` gửi đi = bản đó + `dramaticWeight` mới, nên khoá khác không bị đè bằng bản cũ.
 */
export function diffToOps(before: EditState, after: EditState, opts: { freshBeatMeta?: FilmEventBeatMeta | null } = {}): BatchEditOp[] {
  const ops: BatchEditOp[] = [];
  const eventId = before.eventId;

  const ev: { action?: string; genDurationS?: number | null; beatMeta?: FilmEventBeatMeta } = {};
  if (!same(before.action, after.action)) ev.action = after.action ?? '';
  if (!same(before.genDurationS, after.genDurationS)) ev.genDurationS = after.genDurationS ?? null;
  const weightChanged = normalizeWeight(before.dramaticWeight) !== normalizeWeight(after.dramaticWeight);
  const tacticChanged = !same(before.tactic, after.tactic);
  const initiatorChanged = !same(before.initiator, after.initiator);
  const emotionChanged = !same(before.emotionalEvent, after.emotionalEvent);
  const beatsChanged = !same(
    (before.actionBeats ?? []).map((x) => ({ order: x.order, text: x.text ?? '', kind: x.kind ?? null })),
    (after.actionBeats ?? []).map((x) => ({ order: x.order, text: x.text ?? '', kind: x.kind ?? null })),
  );
  if (weightChanged || tacticChanged || initiatorChanged || emotionChanged || beatsChanged) {
    const next: FilmEventBeatMeta = { ...(opts.freshBeatMeta ?? before.beatMeta ?? {}) };
    if (weightChanged) next.dramaticWeight = normalizeWeight(after.dramaticWeight);
    if (tacticChanged) next.tactic = after.tactic ?? undefined;
    if (initiatorChanged) next.initiator = after.initiator ?? undefined;
    if (emotionChanged) next.emotionalEvent = after.emotionalEvent ?? undefined;
    if (beatsChanged) next.actionBeats = after.actionBeats ?? [];
    ev.beatMeta = next;
  }
  if (Object.keys(ev).length) ops.push({ type: 'updateEvent', eventId, ...ev });

  if (!same(before.lines.map(lineKey), after.lines.map(lineKey))) {
    ops.push({ type: 'replaceDialogue', eventId, lines: after.lines.map(lineKey) });
  }

  const was = new Map(before.shots.map((s) => [s.id, s]));
  for (const s of after.shots) {
    const old = was.get(s.id);
    if (!old) continue; // không thêm shot ở khung mới
    const patch: Partial<Record<ShotField, unknown>> = {};
    for (const k of SHOT_EDIT_FIELDS) if (!same((old as any)[k], (s as any)[k])) patch[k] = (s as any)[k] ?? null;
    if (Object.keys(patch).length) ops.push({ type: 'updateShot', shotId: s.id, ...patch } as BatchEditOp);
  }
  return ops;
}
export const isDirty = (before: EditState, after: EditState): boolean => diffToOps(before, after).length > 0;

// ───────────────────────── cast / thoại ↔ shot (bản sao logic UI cũ) ─────────────────────────

/** Nhân vật có mặt trong video = hợp `presence.visible` của các shot (không trùng, giữ thứ tự gặp đầu tiên). */
export const castOf = (shots: FilmShot[]): string[] => Array.from(new Set(shots.flatMap((s) => (s.presence as any)?.visible || [])));

/**
 * Đặt lại nhân vật của video (BẢN SAO `applyEventCast` ở EventPlanTab.tsx — sửa một bên thì sửa cả hai): bỏ tên ⇒ gỡ khỏi MỌI shot có nó (so không phân biệt hoa thường);
 * thêm tên chưa có ⇒ gắn vào shot ĐẦU. Sửa trực tiếp `shots` của NHÁP; `diffToOps` sẽ ra các `updateShot.presence` (không có `updateEvent.presence`).
 */
/**
 * 261010 P9D I5 — SC5: dàn nhân vật cấp cảnh = hợp visible mọi shot mọi video; áp `applyEventCast` từng video → `updateShot.presence`.
 */
export function sceneCastOpsOf(
  sceneEvents: Array<{ id: string; shots?: FilmShot[] | null }>,
  names: string[],
): BatchEditOp[] {
  const ops: BatchEditOp[] = [];
  const before = new Set(
    sceneEvents.flatMap((ev) => (ev.shots || []).flatMap((s) => ((s.presence as any)?.visible || []) as string[])).map((n) => n.toLowerCase()),
  );
  const removed = Array.from(before).filter((n) => !names.some((x) => x.toLowerCase() === n));
  const added = names.filter((n) => !before.has(n.toLowerCase()));
  for (const ev of sceneEvents) {
    const shots: FilmShot[] = clone(ev.shots || []);
    const cur = Array.from(new Set(shots.flatMap((s) => ((s.presence as any)?.visible || []) as string[])));
    const next = [...cur.filter((n) => !removed.includes(n.toLowerCase())), ...added];
    if (next.length === cur.length && !added.length) continue;
    applyEventCast(shots, next);
    const was = new Map((ev.shots || []).map((s) => [s.id, s]));
    for (const s of shots) {
      const old = was.get(s.id);
      if (!old) continue;
      if (!same((old as any).presence, (s as any).presence)) ops.push({ type: 'updateShot', shotId: s.id, presence: (s as any).presence });
    }
  }
  return ops;
}

export function applyEventCast(shots: FilmShot[], names: string[]): void {
  const keep = new Set(names.map((n) => n.toLowerCase()));
  const present = new Set<string>();
  for (const sh of shots) {
    const p: any = sh.presence || {};
    const vis: string[] = (p.visible || []).filter((n: string) => keep.has(n.toLowerCase()));
    vis.forEach((n) => present.add(n.toLowerCase()));
    if (vis.length !== (p.visible || []).length) sh.presence = { ...p, visible: vis } as any;
  }
  const add = names.filter((n) => !present.has(n.toLowerCase()));
  if (add.length && shots[0]) {
    const p: any = shots[0].presence || {};
    shots[0].presence = { ...p, visible: [...(p.visible || []), ...add] } as any;
  }
}

/** Câu thoại #N (1-based theo event) ↔ shot đang giữ nó (`presence.speaksDialogueIndex`). Dùng hai chiều: ShotCard khoá option đã có chủ, DialogueRow báo câu mồ côi. */
export function lineTakenOf(shots: FilmShot[]): Map<number, { shotId: string; order: number }> {
  const m = new Map<number, { shotId: string; order: number }>();
  for (const s of shots) {
    const i = (s.presence as any)?.speaksDialogueIndex;
    if (typeof i === 'number' && i > 0 && !m.has(i)) m.set(i, { shotId: s.id, order: s.orderIndex });
  }
  return m;
}

// ───────────────────────── cờ "cũ" sau khi lưu ─────────────────────────

interface PlanLike { scenes: Array<{ events: Array<{ id: string; eventOrder: number; staleStages?: unknown; genDurationS?: number | null }> }> }
const eventsOf = (p: PlanLike) => p.scenes.flatMap((s) => s.events);
const stagesOf = (e: { staleStages?: unknown }): string[] => (Array.isArray(e.staleStages) ? e.staleStages.filter((x): x is string => typeof x === 'string') : []);
const STAGE_ORDER = ['prompt', 'clip', 'continuity'];

export interface StaleDelta { eventId: string; eventOrder: number; newStages: string[] }

/**
 * Cờ "cũ" MỚI xuất hiện sau Lưu, so `staleStages` của MỌI video trong plan TRƯỚC / SAU (plan sau = phần `plan` do `batch-edit` trả về).
 * So cả plan vì cờ `continuity` lan sang các video SAU video vừa sửa (`markContinuityStaleFrom`) — so riêng một event sẽ không thấy. Chỉ báo cờ mới; sắp theo `eventOrder`.
 */
export function staleDeltaOf(planBefore: PlanLike, planAfter: PlanLike): StaleDelta[] {
  const was = new Map(eventsOf(planBefore).map((e) => [e.id, new Set(stagesOf(e))]));
  const out: StaleDelta[] = [];
  for (const e of eventsOf(planAfter)) {
    const prev = was.get(e.id);
    if (!prev) continue;
    const fresh = stagesOf(e).filter((s) => !prev.has(s));
    if (!fresh.length) continue;
    const rank = (s: string) => { const i = STAGE_ORDER.indexOf(s); return i < 0 ? STAGE_ORDER.length : i; };
    out.push({ eventId: e.id, eventOrder: e.eventOrder, newStages: Array.from(new Set(fresh)).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)) });
  }
  return out.sort((a, b) => a.eventOrder - b.eventOrder);
}

/** Giây của CHÍNH video có đổi sau Lưu không? `batch-edit` tự tính lại `gen_duration_s` cho video bị đụng (trừ video có `updateEvent.genDurationS`) nên số này có thể đổi dù người sửa không đụng tới. */
export function durationChangeOf(planBefore: PlanLike, planAfter: PlanLike, eventId: string): { from: number | null; to: number | null } | null {
  const a = eventsOf(planBefore).find((e) => e.id === eventId);
  const b = eventsOf(planAfter).find((e) => e.id === eventId);
  if (!a || !b) return null;
  const from = typeof a.genDurationS === 'number' ? a.genDurationS : null;
  const to = typeof b.genDurationS === 'number' ? b.genDurationS : null;
  return from === to ? null : { from, to };
}

/** "Video 6", "Video 6–8", "Video 6–8, 10". */
const rangesText = (orders: number[]): string => {
  const o = Array.from(new Set(orders)).sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < o.length;) {
    let j = i;
    while (j + 1 < o.length && o[j + 1] === o[j] + 1) j++;
    parts.push(j > i ? `${o[i]}–${o[j]}` : `${o[i]}`);
    i = j + 1;
  }
  return `Video ${parts.join(', ')}`;
};
const STAGE_TEXT: Record<string, (where: string) => string> = {
  prompt: (w) => `Prompt đã cũ ở ${w} — dựng lại ở bước 7.`,
  clip: (w) => `Clip đã cũ ở ${w} — tạo clip lại ở bước 8.`,
  continuity: (w) => `Liền mạch đã cũ ở ${w} (nội dung đổi kéo theo các video sau).`,
};
/** Các dòng chữ cho khối "Đã lưu": mỗi loại cờ mới một dòng, kèm video nào và việc làm tiếp ở bước nào. Không có cờ mới ⇒ `[]`. */
export function staleSummaryLines(delta: StaleDelta[]): string[] {
  const lines: string[] = [];
  const stages = Array.from(new Set(delta.flatMap((d) => d.newStages)));
  stages.sort((a, b) => { const r = (s: string) => { const i = STAGE_ORDER.indexOf(s); return i < 0 ? STAGE_ORDER.length : i; }; return r(a) - r(b) || a.localeCompare(b); });
  for (const s of stages) {
    const where = rangesText(delta.filter((d) => d.newStages.includes(s)).map((d) => d.eventOrder));
    lines.push((STAGE_TEXT[s] ?? ((w: string) => `${s} đã cũ ở ${w}.`))(where));
  }
  return lines;
}

// ───────────────────────── lưu: kiểm job → đọc lại → so dấu vân tay → PUT ─────────────────────────

export type PlanWithEvents = EventPlan;
export interface SaveDeps {
  /** Hỏi `workflow-status` NGAY và trả toàn bộ việc nền của plan; `null`/`undefined` = không hỏi được. */
  refreshJobs: () => Promise<Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>> | null | undefined>;
  getEventPlan: (planId: string) => Promise<PlanWithEvents>;
  batchEditPlan: (planId: string, ops: BatchEditOp[]) => Promise<{ plan: PlanWithEvents }>;
}
export type SaveResult =
  | { kind: 'NOOP' }
  | { kind: 'BLOCKED'; reason: string }
  | { kind: 'CHANGED_ELSEWHERE' }
  | { kind: 'SAVED'; ops: BatchEditOp[]; plan: PlanWithEvents; planBefore: PlanWithEvents; delta: StaleDelta[]; duration: { from: number | null; to: number | null } | null };

export const findEventIn = (plan: PlanLike & { scenes: Array<{ events: any[] }> }, eventId: string): FilmEvent | undefined => {
  for (const s of plan.scenes) { const e = s.events.find((x: any) => x.id === eventId); if (e) return e as FilmEvent; }
  return undefined;
};

/**
 * Lưu bản nháp MỘT video. Thứ tự: (không đổi ⇒ NOOP) → hỏi việc nền NGAY (đang có việc ghi plan ⇒ BLOCKED) → `GET /plans/:id/events` mới → so dấu vân tay với bản lúc mở trình sửa
 * (lệch ⇒ CHANGED_ELSEWHERE, KHÔNG gọi `batch-edit`) → `batch-edit` → delta cờ "cũ". Lỗi của `batchEditPlan` được ném tiếp để màn hiện chữ + nút Tải lại.
 * Giới hạn có chủ đích: đây là phát hiện phía client, không phải khoá — một lượt ghi rơi vào giữa lúc đọc lại và lúc PUT vẫn lọt.
 */
export async function saveEventEdit(deps: SaveDeps, a: { planId: string; eventId: string; base: EditState; draft: EditState }): Promise<SaveResult> {
  if (!diffToOps(a.base, a.draft).length) return { kind: 'NOOP' };
  const jobs = await deps.refreshJobs();
  if (!jobs) return { kind: 'BLOCKED', reason: JOBS_UNKNOWN_TEXT };
  const block = planWriteBlock(jobs, a.eventId);
  if (block.blocked) return { kind: 'BLOCKED', reason: block.reason || JOBS_UNKNOWN_TEXT };
  const planBefore = await deps.getEventPlan(a.planId);
  const fresh = findEventIn(planBefore, a.eventId);
  if (!fresh || fingerprintOf(snapshotOf(fresh)) !== fingerprintOf(a.base)) return { kind: 'CHANGED_ELSEWHERE' };
  const ops = diffToOps(a.base, a.draft, { freshBeatMeta: fresh.beatMeta ?? null });
  const { plan } = await deps.batchEditPlan(a.planId, ops);
  return { kind: 'SAVED', ops, plan, planBefore, delta: staleDeltaOf(planBefore, plan), duration: durationChangeOf(planBefore, plan, a.eventId) };
}

/** 261010 P9D EV21 — sửa chữ nhịp i (giữ order + dòng nguồn). Trả bản MỚI. */
export function setBeatText(beats: FilmActionBeat[], i: number, text: string): FilmActionBeat[] {
  return beats.map((b, k) => (k === i ? { ...b, text } : { ...b }));
}
/** 261010 P9D EV21 — đổi chỗ nhịp i với nhịp kề (dir −1 lên / +1 xuống); nhịp mang theo dòng nguồn, `order` đánh lại 1..n. Ngoài biên ⇒ giữ nguyên. */
export function moveBeat(beats: FilmActionBeat[], i: number, dir: -1 | 1): FilmActionBeat[] {
  const j = i + dir;
  const out = beats.map((b) => ({ ...b }));
  if (i < 0 || j < 0 || i >= out.length || j >= out.length) return out;
  [out[i], out[j]] = [out[j], out[i]];
  return out.map((b, k) => ({ ...b, order: k + 1 }));
}

/**
 * 261010 P9D vòng sửa 1 (#1) — đổi chỗ nhịp KÈM remap neo thoại. `afterActionBeatOrder = k` nghĩa "sau nhịp có order k" (0 = trước nhịp đầu, null = chưa neo):
 * đổi chỗ nhịp thì mỗi nhịp mang `order` mới ⇒ mọi neo dương đổi theo ánh xạ order cũ → order mới (0 và null giữ). Trả CẢ HAI để nháp cập nhật trong cùng một lượt
 * (⇒ batch có `updateEvent` + `replaceDialogue`). Ngoài biên ⇒ giữ nguyên.
 */
export function moveBeatWithLines(beats: FilmActionBeat[], lines: FilmDialogueLine[], i: number, dir: -1 | 1): { beats: FilmActionBeat[]; lines: FilmDialogueLine[] } {
  const j = i + dir;
  if (i < 0 || j < 0 || i >= beats.length || j >= beats.length) return { beats: beats.map((b) => ({ ...b })), lines: lines.map((l) => ({ ...l })) };
  const next = beats.map((b) => ({ ...b }));
  [next[i], next[j]] = [next[j], next[i]];
  const oldToNew = new Map<number, number>();
  next.forEach((b, k) => oldToNew.set(b.order, k + 1));
  const outBeats = next.map((b, k) => ({ ...b, order: k + 1 }));
  const outLines = lines.map((l) => {
    const a = l.afterActionBeatOrder;
    return typeof a === 'number' && a > 0 && oldToNew.has(a) ? { ...l, afterActionBeatOrder: oldToNew.get(a)! } : { ...l };
  });
  return { beats: outBeats, lines: outLines };
}
